import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, X, Loader2, ListChecks } from "lucide-react";
import { toast } from "sonner";

const toRows = (values) => values.map((v) => ({ orig: v, value: v }));

const ListEditor = ({ title, name, rows, setRows }) => {
  const [add, setAdd] = useState("");
  const push = () => {
    const v = add.trim();
    if (!v) return;
    if (rows.some((r) => r.value.toLowerCase() === v.toLowerCase())) { toast.error(`"${v}" è già presente`); return; }
    setRows([{ orig: null, value: v }, ...rows]);
    setAdd("");
  };
  return (
    <div className="space-y-2" data-testid={`list-editor-${name}`}>
      <p className="font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground">{title} · {rows.length}</p>
      <div className="flex gap-2">
        <Input value={add} onChange={(e) => setAdd(e.target.value)} onKeyDown={(e) => e.key === "Enter" && push()} placeholder="Nuovo valore" data-testid={`list-add-input-${name}`} />
        <Button size="icon" variant="outline" onClick={push} data-testid={`list-add-btn-${name}`}><Plus className="h-4 w-4" /></Button>
      </div>
      <div className="max-h-80 space-y-1 overflow-y-auto pr-1">
        {rows.map((r, i) => (
          <div key={r.orig ?? `new-${r.value}`} className="flex items-center gap-1">
            <Input value={r.value} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} className="h-8" data-testid={`list-item-${name}-${i}`} />
            <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => setRows(rows.filter((_, j) => j !== i))} data-testid={`list-remove-${name}-${i}`}><X className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
};

const TITLES = { fabric_types: "Tipologie di tessuto", accessory_types: "Tipi di accessorio", colors: "Colori", suppliers: "Fornitori" };

export const ListsDialog = ({ lists, ready, columns }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState({});
  const start = () => { setRows(Object.fromEntries(columns.map((c) => [c, toRows(lists[c] || [])]))); setOpen(true); };
  const renames = (list, rs) => rs.filter((r) => r.orig && r.value.trim() && r.orig !== r.value.trim()).map((r) => ({ list, old: r.orig, new: r.value.trim() }));
  const save = useMutation({
    mutationFn: async () => (await api.put("/materials/lists", {
      ...Object.fromEntries(columns.map((c) => [c, rows[c].map((r) => r.value)])),
      renames: columns.flatMap((c) => renames(c, rows[c])),
    })).data,
    onSuccess: () => { ["material-lists", "materials"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); toast.success("Elenchi salvati"); setOpen(false); },
    onError: () => toast.error("Salvataggio non riuscito"),
  });
  return (
    <>
      <Button variant="outline" onClick={start} disabled={!ready} data-testid="manage-lists-btn"><ListChecks className="mr-2 h-4 w-4" />Gestisci elenchi</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>{columns.map((c) => TITLES[c]).join(", ")}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Aggiungi, rinomina o togli i valori dei menu a tendina. Colori e fornitori sono gli stessi per tessuti e accessori. Se rinomini un valore, viene aggiornato anche sui materiali che lo usano; togliere un valore non modifica i materiali già salvati.</p>
          <div className="grid grid-cols-1 gap-6 py-2 sm:grid-cols-3">
            {columns.map((c) => <ListEditor key={c} title={TITLES[c]} name={c} rows={rows[c] || []} setRows={(v) => setRows((r) => ({ ...r, [c]: v }))} />)}
          </div>
          <DialogFooter><Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="lists-save-btn">{save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
